import React, { useState, useCallback } from "react";
import { css } from '@emotion/core'
import moment from "moment";
import { useTranslation } from "react-i18next";
import useAxios from 'axios-hooks';
import Spinner from "../../Spinner";
import Pagination from "../../Pagination";
import EmailDetailModal from "./EmailDetailModal";
import { SearchStateContext } from '../../../context/SearchContext'

const DEFAULT_PAGINATION = {
  limit: 10,
  offset: 0,
}

const EmailsTab = ({ studentId, isActive }) => {
  const { t } = useTranslation();
  const [displayEmailModal, setDisplayEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState();
  const [enrollmentOptions, setEnrollmentOptions] = useState([]);
  const [selectEnrollmentId, setSelectEnrollmentId] = useState('');
  const [count, setCount] = useState(0);
  const { setWithSearch } = React.useContext(SearchStateContext)
  const [pagination, setPagination] = useState({
    limit: 10,
    offset: 0,
  });

  // REST API calls
  const [{ loading: loadingData, data: studentMailData }, fetchEmails] = useAxios(
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem('jwt')}`
      }
    },
    { manual: true }
  );

  const [{ data: emailPeriodsData }, fetchEmailPeriods] = useAxios(
    {
      url: `${process.env.REACT_APP_BASE_URL}api/v1/students/${studentId}/email-periods`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem('jwt')}`
      }
    },
    { manual: true }
  );

  React.useEffect(() => {
    if (isActive) {
      fetchEmailPeriods();
    }
  }, [isActive])

  React.useEffect(() => {
    if (emailPeriodsData && emailPeriodsData.length > 0) {
      let enrollmentsOptions = []
      emailPeriodsData.forEach(period =>
        enrollmentsOptions.push({
          value: period.id,
          label: `${period.enrollment_period.academic_year} - ${period.segment_label}`
        })
      )
      setSelectEnrollmentId(enrollmentsOptions[0].value)
      setEnrollmentOptions(enrollmentsOptions)
      fetchEmails({
        url: `${process.env.REACT_APP_BASE_URL}api/v1/students/${studentId}/emails`,
        params: {
          'filter[enrollment_period_segment_id]': enrollmentsOptions[0].value,
          perPage: DEFAULT_PAGINATION.limit,
          page: 1
        }
      })
      setCount(c => (c + 1));
    }
  }, [emailPeriodsData])

  React.useEffect(() => {
    setWithSearch(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOpenEmailModal = useCallback((email) => {
    setSelectedEmail(email);
    setDisplayEmailModal(true);
  }, [])

  const handleCloseEmailModal = useCallback(() => {
    setSelectedEmail();
    setDisplayEmailModal(false);
  }, [])

  const onPageChange = useCallback((newPage) => {
    fetchEmails({
      url: `${process.env.REACT_APP_BASE_URL}api/v1/students/${studentId}/emails`,
      params: {
        'filter[enrollment_period_segment_id]': selectEnrollmentId,
        perPage: pagination.limit,
        page: newPage
      }
    })
    setPagination(p => ({
      ...p,
      offset: (newPage - 1) * p.limit
    }))
  }, [pagination, selectEnrollmentId])

  const handleChangePeriod = useCallback((periodId) => {
    setSelectEnrollmentId(periodId)
    fetchEmails({
      url: `${process.env.REACT_APP_BASE_URL}api/v1/students/${studentId}/emails`,
      params: {
        'filter[enrollment_period_segment_id]': periodId,
        perPage: DEFAULT_PAGINATION.limit,
        page: 1
      }
    })
    setPagination({
      limit: DEFAULT_PAGINATION.limit,
      offset: DEFAULT_PAGINATION.offset
    })
  }, [])

  const totalCount = studentMailData ? studentMailData.total : 0;
  const emailList = studentMailData ? studentMailData.data : [];

  return (
    <>
      {selectedEmail && (
        <EmailDetailModal
          display={displayEmailModal}
          setDisplay={handleCloseEmailModal}
          openEmail={selectedEmail}
          studentId={studentId}
        />
      )}
      <div className="container">
        <div className="row form-type-material">
          <div className="col-4">
            <div
              className='form-group do-float'
              key={count}
              css={css`
                  .form-type-material .bootstrap-select .dropdown-toggle:focus {
                    background-color: transparent !important;
                  }
                `}>
              <select
                type='text'
                name='schoolId'
                id='schoolId'
                className='form-control bg-transparent'
                css={css`
                  .dropdown-menu {
                    max-height: 300px !important;
                  }
                `}
                data-provide='selectpicker'
                onChange={e => handleChangePeriod(e.target.value)}
                value={selectEnrollmentId}
              >
                {enrollmentOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label htmlFor='schoolId' className='label-floated'>
                {t('ENROLLMENT_PERIOD')}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="card card-shadowed">
        <div className="card-header">
          <h5 className="card-title fw-500">{t("EMAIL_LOG")}</h5>
        </div>
        <div className="card-body">
          {loadingData ? (
            <Spinner />
          ) : (
            <table className="table-striped table table-hover">
              <thead>
                <tr>
                  <th>{t("VIEWED")}</th>
                  <th>{t("EMAIL_SUBJECT")}</th>
                  <th>{t("TYPE")}</th>
                  <th>{t("SENT")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {emailList.map(email => (
                    <tr key={email.id}>
                      <td>
                        {email.viewed
                          ? moment(email.viewed).format("MM/DD/YYYY")
                          : "No"}
                      </td>
                      <td>{email.subject}</td>
                      <td>{email.email_type}</td>
                      <td>{moment(email.created_at).format("MM/DD/YYYY")}</td>
                      <td>
                        <button
                          className="btn btn-bold btn-label btn-primary"
                          onClick={() => handleOpenEmailModal(email)}
                        >
                          <span>
                            <i className="ti-check"></i>
                          </span>
                          {t("VIEW")}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {studentMailData && (
        <div className="mb-100">
          <Pagination
            pages={Math.ceil(totalCount / pagination.limit)}
            onPageChange={onPageChange}
            from={totalCount > 0
              ? pagination.offset + 1
              : null
            }
            to={totalCount === 0
              ? null
              : totalCount < pagination.offset + pagination.limit
                ? pagination.offset + (totalCount % pagination.limit)
                : pagination.offset + pagination.limit}
            total={totalCount}
          />
        </div>
      )}
      {!studentMailData && (
        <div className="mb-100">
          <Pagination
            pages={0}
            onPageChange={onPageChange}
            from={null}
            to={null}
            total={0}
          />
        </div>
      )}
    </>
  );
};

export default EmailsTab;
